import {
  type CSSProperties,
  type ForwardedRef,
  forwardRef,
  useMemo,
} from "react";

import clsx from "clsx";
import { twMerge } from "tailwind-merge";
import { useElementSize } from "usehooks-ts";

import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { useFeatureFlags } from "@hooks/http";
import { useFuzzySearch } from "@hooks/use-fuzzy-search";

import { TextInput } from "../text-input";

import { Dropdown } from "./dropdown";
import { OptionItem } from "./option-item";
import { type BaseProps } from "./types";

interface Props<T> extends Omit<BaseProps<T>, "buttonClassName"> {
  value: T | null;
  placeholder?: string;
  inputWrapperClassName?: string;
  onChange: (val: T | null) => void;
}

const _Autocomplete = <T,>(
  {
    value,
    options,
    placeholder,
    disabled,
    clearable,
    size = "sm",
    hideCaret,
    inputWrapperClassName,
    dropdownClassName,
    itemClassName,
    onChange,
  }: Props<T>,
  ref: ForwardedRef<HTMLDivElement>,
) => {
  const { hasFeatureFlag } = useFeatureFlags();
  const [sizeRef, { width }] = useElementSize();

  const selectedOption = useMemo(() => {
    return options.find((e) => e.value === value) ?? null;
  }, [options, value]);

  const [filteredOptions, { setSearch }] = useFuzzySearch(options, {
    searchKeys: ["label"],
  });

  return (
    <Combobox
      as="div"
      ref={ref}
      immediate
      disabled={disabled}
      className="inline-block"
      value={selectedOption}
      virtual={{
        options: filteredOptions,
        disabled: (option) => option?.disabled ?? false,
      }}
      onChange={(option) => {
        if (option) {
          onChange(option.value);
        }
      }}
      onClose={() => setSearch("")}
    >
      {({ open }) => (
        <>
          <ComboboxButton>
            <ComboboxInput
              as={TextInput}
              wrapperRef={sizeRef}
              wrapperClassName={inputWrapperClassName}
              className={clsx("cursor-pointer", {
                "placeholder:text-v2-primary": open,
              })}
              size={size}
              placeholder={placeholder ?? "Select.."}
              displayValue={(val: { value: T } | null) =>
                options.find((e) => e.value === val?.value)?.label ?? ""
              }
              onChange={(event) => setSearch(event.target.value)}
              rightIcon={
                <>
                  {clearable && selectedOption && (
                    <i
                      className={twMerge(
                        clsx(
                          "fa-regular fa-times-circle text-sm text-v2-cosmic/400 cursor-pointer",
                          "hover:text-v2-cosmic/600",
                          { "text-v2-gray/200": disabled },
                        ),
                      )}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                      }}
                      onClick={() => {
                        onChange(null);
                      }}
                    />
                  )}
                  {!hideCaret && (!clearable || !selectedOption) && (
                    <i
                      className={twMerge(
                        clsx(
                          "fa-regular fa-angle-down text-base text-v2-primary",
                          {
                            "text-v2-cosmic/400 rotate-180": open,
                            "transition-[transform] duration-200 ease-out":
                              open,
                            "text-v2-disabled": disabled,
                          },
                        ),
                      )}
                    />
                  )}
                </>
              }
            />
          </ComboboxButton>
          {filteredOptions.length > 0 && (
            <ComboboxOptions
              as={Dropdown}
              anchor={{ to: "bottom start", gap: 5 }}
              transition
              className={clsx(
                "w-[var(--input-wrapper-width)]",
                dropdownClassName,
                {
                  "bg-white": !hasFeatureFlag("enableDashboardV2"),
                },
              )}
              style={
                {
                  "--input-wrapper-width": `${width - 2}px`,
                } as CSSProperties
              }
            >
              {({ option }) => (
                <ComboboxOption
                  as={OptionItem}
                  key={String(option.value)}
                  size={size}
                  disabled={option.disabled}
                  value={option}
                  option={option}
                  className={itemClassName}
                />
              )}
            </ComboboxOptions>
          )}
        </>
      )}
    </Combobox>
  );
};

// workaround to correctly infer generic type for forward reffed component
export const Autocomplete = forwardRef(_Autocomplete) as <T extends string>(
  props: Props<T> & { ref?: ForwardedRef<HTMLDivElement> },
) => ReturnType<typeof _Autocomplete>;
